<template>
  <div>
    <h1 class="m-3">
      {{ $t("global.exportInProgress") }}
    </h1>
    <table-renderer v-for="data of datas" :key="data.tableNumber" class="export" :table-number="data.tableNumber"
      :data-table="data.dataTable" :dimension-fields="data.dimensionFields" :measure-fields="data.measureFields"
      :group-headers="data.groupHeaders" :table-header="data.tableHeader" :top-group-headers="data.topGroupHeaders"
      :default-sorted-column="data.defaultSortedColumn" :averages-data="data.averagesData"
      :totals-data="data.totalsData" :data-ready="dataReady"
      :hide-groups="hideGroupsByTableNumber[data.tableNumber] === true" :custom-table-class="data.customTableClass"
      style="display: none" />
  </div>
</template>

<script>
import TableRenderer from "@/components/TableRenderer.vue";
import TableTools from "@/utils/tableTools";
import { exportHelper } from "@/utils/exportHelper";
import { ExportTemplatePdf } from "@/router/routeNames";
import dimensionEnum from "@/model/dimensionEnum";
export default {
  name: ExportTemplatePdf,
  components: {
    TableRenderer,
  },
  data: function () {
    return {
      datas: [],
      title: "",
      subtitles: [],
      filename: "Reporting",
      hideGroupsByTableNumber: {},
      dataReady: true,
      orientationPaysage: true,
      reduceHeader: false,
    };
  },
  created() {
    const self = this;
    var exportData = JSON.parse(localStorage.exportData);

    exportData.tables.forEach(function (data, i) {
      if (!data.tableNumber) data.tableNumber = i.toString();

      //Traitement des formats
      data.dimensionFields = data.dimensionFields.map((col) => {
        col.formatter = (value, key, item) => {
          const siteCurrency = item[dimensionEnum.SITE_CURRENCY] || 'EUR';
          return TableTools.Helper.valueFormatter(value, key, false, siteCurrency);
        };
        col.tdClass = (value, key) => {
          const baseClass = TableTools.Helper.cellStyleFormatter(value, key);
          return [baseClass, "export-dimension"].filter(Boolean).join(" ");
        };
        col.thClass = "export-dimension";
        return col;
      });

      data.measureFields = data.measureFields.map((col) => {
        col.formatter = (value, key, item) => {
          const siteCurrency = item[dimensionEnum.SITE_CURRENCY] || 'EUR';
          return TableTools.Helper.valueFormatter(value, key, false, siteCurrency);
        };
        col.tdClass = (value, key) => {
          return TableTools.Helper.cellStyleFormatter(value, key);
        };
        return col;
      });

      // Si le tableau a trop de colonnes, découper
      const MAX_COLUMNS = 13;
      self.pushSplitedTables(data, MAX_COLUMNS);
      self.addSpacerSiteChange(exportData.tables, i, data);
    });
    self.title = exportData.title;
    if (exportData.subtitles && Array.isArray(exportData.subtitles))
      self.subtitles = exportData.subtitles;
    if (exportData.filename) self.filename = exportData.filename;
    if (exportData.hideGroupsByTableNumber)
      self.hideGroupsByTableNumber = exportData.hideGroupsByTableNumber;
    if (exportData.reduceHeader) self.reduceHeader = exportData.reduceHeader;
  },
  mounted: async function () {
    await this.createPdf();
  },
  methods: {
    pushSplitedTables(data, maxColumns) {
      // Calcule le nombre total de colonnes
      const totalColumns = data.dimensionFields.length + data.measureFields.length;
      if (totalColumns <= maxColumns) {
        this.datas.push(data);
        return;
      }

      const keyDimensions = data.dimensionFields.slice(
        0,
        Math.min(2, data.dimensionFields.length)
      );
      // Si topGroupHeaders est présent, on découpe en fonction de lui (sans limite MAX_COLUMNS)
      const useTopGroupHeaders = data.topGroupHeaders && data.topGroupHeaders.length > 0;

      let measureIndex = 0;
      let groupHeaderIndex = 0;
      let topGroupHeaderIndex = 0;
      let partNumber = 1;

      while (measureIndex < data.measureFields.length) {
        // Pour la première partie, on garde toutes les dimensions, sinon juste les clés
        const partDimensions =
          partNumber === 1 ? data.dimensionFields : keyDimensions;

        const partCounts = useTopGroupHeaders
          ? this.getTopGroupPartCounts(data, topGroupHeaderIndex, groupHeaderIndex)
          : this.getStandardPartCounts(
            data,
            groupHeaderIndex,
            maxColumns - partDimensions.length,
            measureIndex
          );

        this.datas.push(
          this.buildSplitPart(data, {
            partNumber,
            partDimensions,
            measureIndex,
            partMeasuresCount: partCounts.partMeasuresCount,
            groupHeaderIndex,
            partGroupHeadersCount: partCounts.partGroupHeadersCount,
            useTopGroupHeaders,
            topGroupHeaderIndex,
            partTopGroupHeadersCount: partCounts.partTopGroupHeadersCount,
          })
        );

        measureIndex += partCounts.partMeasuresCount;
        groupHeaderIndex += partCounts.partGroupHeadersCount;
        topGroupHeaderIndex += partCounts.partTopGroupHeadersCount;
        partNumber++;
      }
    },

    getTopGroupPartCounts(data, topGroupHeaderIndex, groupHeaderIndex) {
      let partMeasuresCount = 0;
      let partGroupHeadersCount = 0;
      let partTopGroupHeadersCount = 0;

      if (topGroupHeaderIndex >= data.topGroupHeaders.length) {
        return {
          partMeasuresCount,
          partGroupHeadersCount,
          partTopGroupHeadersCount,
        };
      }

      const topHeader = data.topGroupHeaders[topGroupHeaderIndex];
      // Mode topGroupHeaders : on prend des topGroupHeaders complets sans limite MAX_COLUMNS
      partMeasuresCount = topHeader.nbOfMeasures || 1;
      partTopGroupHeadersCount = 1;

      if (data.groupHeaders && data.groupHeaders.length > 0) {
        // Calcule aussi combien de groupHeaders correspondent à ces mesures
        let tempMeasureCount = 0;
        let tempGroupIndex = groupHeaderIndex;

        while (
          tempGroupIndex < data.groupHeaders.length &&
          tempMeasureCount < partMeasuresCount
        ) {
          const groupHeaderMeasureCount =
            data.groupHeaders[tempGroupIndex].nbOfMeasures || 1;
          tempMeasureCount += groupHeaderMeasureCount;
          partGroupHeadersCount++;
          tempGroupIndex++;
        }
      }

      return {
        partMeasuresCount,
        partGroupHeadersCount,
        partTopGroupHeadersCount,
      };
    },

    getStandardPartCounts(data, groupHeaderIndex, availableColumns, measureIndex) {
      let partMeasuresCount = 0;
      let partGroupHeadersCount = 0;
      const partTopGroupHeadersCount = 0;

      // Mode normal : utilise MAX_COLUMNS et découpe selon groupHeaders
      if (data.groupHeaders && data.groupHeaders.length > 0) {
        let tempGroupIndex = groupHeaderIndex;
        while (tempGroupIndex < data.groupHeaders.length) {
          const headerMeasureCount =
            data.groupHeaders[tempGroupIndex].nbOfMeasures || 1;
          if (partMeasuresCount + headerMeasureCount <= availableColumns) {
            partMeasuresCount += headerMeasureCount;
            partGroupHeadersCount++;
            tempGroupIndex++;
          } else {
            break;
          }
        }
      } else {
        partMeasuresCount = Math.min(
          availableColumns,
          data.measureFields.length - measureIndex
        );
      }

      return {
        partMeasuresCount,
        partGroupHeadersCount,
        partTopGroupHeadersCount,
      };
    },

    buildSplitPart(
      data,
      {
        partNumber,
        partDimensions,
        measureIndex,
        partMeasuresCount,
        groupHeaderIndex,
        partGroupHeadersCount,
        useTopGroupHeaders,
        topGroupHeaderIndex,
        partTopGroupHeadersCount,
      }
    ) {
      // Découpage de la partie du tableau
      return {
        ...data,
        tableNumber: `${data.tableNumber}_${partNumber}`,
        dimensionFields: partDimensions,
        measureFields: data.measureFields.slice(
          measureIndex,
          measureIndex + partMeasuresCount
        ),
        groupHeaders:
          data.groupHeaders && partGroupHeadersCount > 0
            ? data.groupHeaders.slice(
              groupHeaderIndex,
              groupHeaderIndex + partGroupHeadersCount
            )
            : undefined,
        topGroupHeaders:
          useTopGroupHeaders && partTopGroupHeadersCount > 0
            ? data.topGroupHeaders.slice(
              topGroupHeaderIndex,
              topGroupHeaderIndex + partTopGroupHeadersCount
            )
            : undefined,
      };
    },

    addSpacerSiteChange(tables, index, currentData) {
      if (index >= tables.length - 1) return;

      const nextData = tables[index + 1];
      const currentSite = currentData.tableHeader || currentData.tableNumber;
      const nextSite = nextData.tableHeader || nextData.tableNumber;

      if (currentSite !== nextSite) {
        this.datas.push({
          tableNumber: `spacer_${index}`,
          dataTable: [],
          dimensionFields: [],
          measureFields: [],
        });
      }
    },

    async createPdf() {
      let doc = exportHelper.createPdfDoc(
        this.datas.map((d) => d.tableNumber),
        null,
        {
          title: this.title,
          subtitles: this.subtitles,
          orientationPaysage: this.orientationPaysage,
          reduceHeader: this.reduceHeader,
        }
      );

      setTimeout(async () => {
        await doc.save(`${this.filename}.pdf`, {
          returnPromise: true,
        });
        setTimeout(() => {
          window.close();
        }, 500);
      }, 200);
    },
  },
};
</script>
